import * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Typography Components for Modern UI V2
 *
 * These components provide consistent typography across the application,
 * with JetBrains Mono for financial data and proper hierarchy for headings.
 */

/**
 * MonoText - For financial data, numbers, currency, and percentages
 * Uses JetBrains Mono for clear number rendering
 */
const MonoText = React.forwardRef(({
  className,
  as: Component = "span",
  size = "base",
  weight = "medium",
  children,
  ...props
}, ref) => {
  const sizeClasses = {
    xs: "text-xs",
    sm: "text-sm",
    base: "text-base",
    lg: "text-lg",
    xl: "text-xl",
    "2xl": "text-2xl",
    "3xl": "text-3xl",
    "4xl": "text-4xl",
  };

  const weightClasses = {
    normal: "font-normal",
    medium: "font-medium",
    semibold: "font-semibold",
    bold: "font-bold",
  };

  return (
    <Component
      ref={ref}
      className={cn(
        "font-mono tabular-nums",
        sizeClasses[size],
        weightClasses[weight],
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
});
MonoText.displayName = "MonoText";

/**
 * Heading - For section titles with proper hierarchy
 * Supports levels 1-6 with automatic sizing
 *
 * Typography scale (refined for clearer visual distinction):
 * - Level 1: text-5xl (3rem) - Page titles, main headings
 * - Level 2: text-3xl (1.875rem) - Section headings
 * - Level 3: text-xl (1.25rem) - Card headings, subsections
 * - Level 4: text-lg (1.125rem) - Minor headings, labels
 * - Level 5: text-base (1rem) - Small headings
 * - Level 6: text-sm (0.875rem) - Captions
 */
const Heading = React.forwardRef(({
  className,
  level = 2,
  children,
  ...props
}, ref) => {
  const Component = `h${level}`;

  // Refined typography scale with clearer visual jumps between levels
  const levelClasses = {
    1: "text-5xl font-bold tracking-tight leading-tight",      // Page titles
    2: "text-3xl font-semibold tracking-tight leading-snug",   // Section headings
    3: "text-xl font-semibold leading-snug",                   // Card headings
    4: "text-lg font-medium leading-normal",                   // Subsection headings
    5: "text-base font-medium leading-normal",                 // Minor headings
    6: "text-sm font-medium leading-relaxed",                  // Caption headings
  };

  return (
    <Component
      ref={ref}
      className={cn(
        "text-foreground",
        levelClasses[level],
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
});
Heading.displayName = "Heading";

/**
 * Text - For body text with variants
 * Supports primary, secondary, muted, and emphasis variants
 */
const Text = React.forwardRef(({
  className,
  as: Component = "p",
  variant = "primary",
  size = "base",
  children,
  ...props
}, ref) => {
  const variantClasses = {
    primary: "text-foreground",
    secondary: "text-muted-foreground",
    muted: "text-muted-foreground/80", // Changed from /70 for better WCAG AA contrast
    emphasis: "text-foreground font-medium",
    success: "text-success",
    error: "text-destructive",
    warning: "text-warning",
    info: "text-info",
  };

  const sizeClasses = {
    xs: "text-xs",
    sm: "text-sm",
    base: "text-base",
    lg: "text-lg",
    xl: "text-xl",
  };

  return (
    <Component
      ref={ref}
      className={cn(
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
});
Text.displayName = "Text";

/**
 * Label - For form labels and captions
 * Smaller text with proper weight
 */
const Label = React.forwardRef(({
  className,
  children,
  ...props
}, ref) => {
  return (
    <span
      ref={ref}
      className={cn(
        "text-sm font-medium text-muted-foreground",
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
});
Label.displayName = "Label";

/**
 * Caption - For small helper text and descriptions
 */
const Caption = React.forwardRef(({
  className,
  children,
  ...props
}, ref) => {
  return (
    <span
      ref={ref}
      className={cn(
        "text-xs text-muted-foreground/80",
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
});
Caption.displayName = "Caption";

export { MonoText, Heading, Text, Label, Caption };
